import { type ButtonHTMLAttributes, type HTMLAttributes, type ImgHTMLAttributes, type InputHTMLAttributes, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { AlertCircle, Check, ChevronRight, LoaderCircle, Search, TriangleAlert, X } from 'lucide-react'
import { useAppStore } from '../app/store'

export function Button({ className = '', variant = 'primary', type = 'button', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger' }) {
  return <button type={type} className={`button button--${variant} ${className}`} {...props} />
}

export function IconButton({ label, className = '', type = 'button', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return <button type={type} className={`icon-button ${className}`} aria-label={label} title={label} {...props} />
}

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return <label className="field"><span className="field__label">{label}</span>{children}{error ? <span className="field__error">{error}</span> : hint ? <span className="field__hint">{hint}</span> : null}</label>
}

export function Input({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) { return <input className={`input ${className}`} {...props} /> }
export function NumberInput({ inputMode, step, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  const decimal = String(step ?? '').includes('.')
  return <Input type="number" inputMode={inputMode ?? (decimal ? 'decimal' : 'numeric')} step={step} {...props} />
}
export function Select({ className = '', ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) { return <select className={`input select ${className}`} {...props} /> }

export function SearchField(props: InputHTMLAttributes<HTMLInputElement>) {
  return <label className="search-field"><Search size={18} aria-hidden="true" /><span className="sr-only">搜索</span><input {...props} /></label>
}

export function Card({ className = '', ...props }: HTMLAttributes<HTMLElement>) { return <section className={`card ${className}`} {...props} /> }

export function StatCard({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return <Card className="stat-card"><span>{label}</span><strong>{value}</strong>{hint && <small>{hint}</small>}</Card>
}

export function Badge({ tone = 'neutral', children }: { tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info'; children: ReactNode }) {
  return <span className={`badge badge--${tone}`}>{children}</span>
}

export function ColorSwatch({ hex, size = 'md' }: { hex?: string | null | undefined; size?: 'sm' | 'md' | 'lg' }) {
  const color = hex ? (hex.startsWith('#') ? hex : `#${hex}`) : '#d9d2c7'
  return <svg className={`swatch swatch--${size}`} viewBox="0 0 32 32" aria-hidden="true"><circle cx="16" cy="16" r="15" fill={color} /><circle cx="16" cy="16" r="5" fill="white" fillOpacity=".44" /></svg>
}

export function SafeImage({ fallback, alt, ...props }: ImgHTMLAttributes<HTMLImageElement> & { fallback?: ReactNode }) {
  const [failed, setFailed] = useState(false)
  useEffect(() => setFailed(false), [props.src])
  if (failed || !props.src) return <span className="image-placeholder" role="img" aria-label={alt || '图片无法显示'}>{fallback ?? <AlertCircle />}</span>
  return <img {...props} alt={alt} onError={() => setFailed(true)} />
}

export function ColorCard({ code, hex, series, quantities, status, onClick }: {
  code: string; hex?: string | null; series?: string | null
  quantities: { physical: number; planned: number; available: number }
  status: { label: string; tone: 'success' | 'warning' | 'danger' | 'neutral' }
  onClick: () => void
}) {
  return <button className="color-card" onClick={onClick}>
    <span className="color-card__top"><ColorSwatch hex={hex} size="lg" /><span><strong className="mono">{code}</strong><small>{series || '未分系列'}</small></span><ChevronRight size={18} /></span>
    <span className="color-card__numbers"><span>实物 <b>{quantities.physical.toLocaleString()}</b></span><span>计划 <b>{quantities.planned.toLocaleString()}</b></span><span>可用 <b>{quantities.available.toLocaleString()}</b></span></span>
    <Badge tone={status.tone}>{status.label}</Badge>
  </button>
}

export function EmptyState({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return <div className="state"><h3>{title}</h3><p>{description}</p>{action}</div>
}

export function LoadingState({ label = '正在读取…' }: { label?: string }) {
  return <div className="state state--loading" role="status"><LoaderCircle className="spin" /><p>{label}</p></div>
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const message = error instanceof Error ? error.message : '暂时无法读取数据'
  return <div className="state state--error" role="alert"><AlertCircle /><h3>这里没有加载成功</h3><p>{message}</p>{retry && <Button variant="secondary" onClick={retry}>重试</Button>}</div>
}

export function Dialog({ open, title, description, children, onClose, wide = false }: { open: boolean; title: string; description?: string; children: ReactNode; onClose: () => void; wide?: boolean }) {
  const titleId = useId()
  const descriptionId = useId()
  const panel = useRef<HTMLDivElement>(null)
  const returnFocus = useRef<HTMLElement | null>(null)
  const onCloseRef = useRef(onClose)

  useEffect(() => { onCloseRef.current = onClose }, [onClose])

  useEffect(() => {
    if (!open) return
    returnFocus.current = document.activeElement as HTMLElement | null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const node = panel.current
    node?.querySelector<HTMLElement>('button, input, select, textarea, [tabindex="0"]')?.focus()
    const onKey = (event: KeyboardEvent) => {
      const dialogs = document.querySelectorAll<HTMLElement>('[role="dialog"]')
      if (dialogs[dialogs.length - 1] !== node) return
      if (event.key === 'Escape') onCloseRef.current()
      if (event.key !== 'Tab' || !node) return
      const focusable = [...node.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]')]
      const first = focusable[0]
      const last = focusable.at(-1)
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = previousOverflow; returnFocus.current?.focus() }
  }, [open])

  if (!open) return null
  return <div className="dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
    <div ref={panel} className={`dialog ${wide ? 'dialog--wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={description ? descriptionId : undefined}>
      <header className="dialog__header"><div><h2 id={titleId}>{title}</h2>{description && <p id={descriptionId}>{description}</p>}</div><IconButton label="关闭" onClick={onClose}><X /></IconButton></header>
      <div className="dialog__body">{children}</div>
    </div>
  </div>
}

export const Drawer = Dialog

export function ConfirmDialog({ open, title, description, confirmLabel, pending = false, onClose, onConfirm }: {
  open: boolean
  title: string
  description: ReactNode
  confirmLabel: string
  pending?: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  return <Dialog open={open} title={title} onClose={() => { if (!pending) onClose() }}>
    <div className="confirm-dialog">
      <div className="confirm-dialog__message"><TriangleAlert aria-hidden="true" /><p>{description}</p></div>
      <div className="dialog-actions"><Button variant="secondary" disabled={pending} onClick={onClose}>取消</Button><Button variant="danger" disabled={pending} onClick={onConfirm}>{pending ? '正在处理…' : confirmLabel}</Button></div>
    </div>
  </Dialog>
}

export function Tabs({ value, onChange, items, label = '内容筛选' }: { value: string; onChange: (value: string) => void; items: { value: string; label: string; count?: number }[]; label?: string }) {
  const onKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
    event.preventDefault()
    const buttons = [...(event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? [])]
    const current = buttons.indexOf(event.currentTarget)
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (current + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length
    buttons[next]?.focus()
    buttons[next]?.click()
  }
  return <div className="tabs" role="tablist" aria-label={label}>{items.map((item) => <button type="button" key={item.value} role="tab" tabIndex={value === item.value ? 0 : -1} aria-selected={value === item.value} onKeyDown={onKeyDown} onClick={() => onChange(item.value)}>{item.label}{item.count !== undefined && <span>{item.count}</span>}</button>)}</div>
}

export function Timeline({ items }: { items: { id: string | number; title: string; detail: string; time: string; positive?: boolean }[] }) {
  return <ol className="timeline">{items.map((item) => <li key={item.id}><span className={item.positive ? 'timeline__dot timeline__dot--positive' : 'timeline__dot'} /><div><strong>{item.title}</strong><p>{item.detail}</p><time>{item.time}</time></div></li>)}</ol>
}

export function Toast() {
  const toast = useAppStore((state) => state.toast)
  const clear = useAppStore((state) => state.clearToast)
  useEffect(() => { if (!toast) return; const timer = window.setTimeout(clear, 3600); return () => window.clearTimeout(timer) }, [toast, clear])
  if (!toast) return null
  const Icon = toast.tone === 'success' ? Check : toast.tone === 'error' ? AlertCircle : TriangleAlert
  return <div className={`toast toast--${toast.tone}`} role={toast.tone === 'error' ? 'alert' : 'status'}><Icon size={18} aria-hidden="true" /><span>{toast.message}</span><IconButton label="关闭提示" onClick={clear}><X size={16} /></IconButton></div>
}

export function PageHeader({ eyebrow, title, description, action }: { eyebrow?: string; title: string; description?: string; action?: ReactNode }) {
  return <header className="page-header"><div>{eyebrow && <span className="eyebrow">{eyebrow}</span>}<h1>{title}</h1>{description && <p>{description}</p>}</div>{action && <div className="page-header__action">{action}</div>}</header>
}
